// Hidratacion del cache de lectura desde los snapshots offline (cold-start).
//
// GET /api/sync/snapshot y GET /api/sync/catalogs devuelven DTOs CRUDOS del
// backend. Las pantallas, en cambio, leen con createQuery({ queryKey:
// queryKeys.X(estId), queryFn: getAllX }) y cachean el resultado de aplicar su
// process* a esos DTOs.
//
// Para que la app funcione offline SIN haber visitado cada pantalla online antes
// (cold-start: instalar -> login con red -> salir al campo sin senal), sembramos
// cada queryKey con el array crudo transformado por el MISMO process* que usa la
// queryFn de esa pantalla -> la shape cacheada queda identica a una lectura
// online, y TanStack la sirve offline.
//
// Nota de id (gotcha #4 del plan): estId == loadStorageEstablecimiento().id ==
// el cab.id que usan las pantallas que leen de "establecimiento". Es el mismo
// valor que el login pasa al prefetch, asi que las keys matchean.
import { queryClient } from "./queryClient";
import { queryKeys } from "./keys";
import { processAnimales } from "$lib/java/animales/animalesback";
import {
    processTreatmentEvents,
    processTipos,
} from "$lib/java/tratamientos/tratamientosback";
import { processTactos } from "$lib/java/tactos/tactosback";
import { processNacimientos } from "$lib/java/nacimientos/nacimientosback";
import { processServicios } from "$lib/java/servicios/serviciosback";
import { processComments } from "$lib/java/observaciones/observacionesback";
import { processPesos } from "$lib/java/pesajes/pesajesback";
import { processLotsResponse } from "$lib/java/lotes/lotesback";
import { processHerdsResponse } from "$lib/java/rodeos/rodeosback";

// Siembra una key solo si esta vacia: no pisa datos persistidos de una sesion
// previa ni filas optimistas pendientes de sincronizar.
function seedKey(key, rows, mapper) {
    if (!Array.isArray(rows)) return;
    if (queryClient.getQueryData(key) !== undefined) return;
    queryClient.setQueryData(key, mapper(rows));
}

// Snapshot del establecimiento: animales + eventos recientes (listas).
export function seedFromSnapshot(estId, snapshot) {
    if (!estId || !snapshot) return;
    seedKey(queryKeys.animals(estId), snapshot.animals, processAnimales);
    seedKey(queryKeys.treatments(estId), snapshot.recentTreatmentEvents, processTreatmentEvents);
    seedKey(queryKeys.pregnancyChecks(estId), snapshot.recentPregnancyChecks, processTactos);
    seedKey(queryKeys.births(estId), snapshot.recentBirths, processNacimientos);
    seedKey(queryKeys.services(estId), snapshot.recentServices, processServicios);
    // processComments filtra isActive y mapea; cabid omitido (el snapshot ya
    // viene scopeado por el JWT), igual que la queryFn online (getAll() sin arg).
    seedKey(queryKeys.observations(estId), snapshot.recentComments, (r) => processComments(r));
    seedKey(queryKeys.weights(estId), snapshot.recentWeights, (r) => processPesos(r));
}

// Catalogos: los selectores de las pantallas de ALTA (tipos de tratamiento,
// lotes, rodeos), para poder CREAR eventos offline en cold-start. Las categorias
// no van: son un array estatico hardcodeado en el front, no vienen del backend.
export function seedFromCatalogs(estId, catalogs) {
    if (!estId || !catalogs) return;
    seedKey(queryKeys.treatmentTypes(estId), catalogs.treatmentTypes, (r) => processTipos(r, estId));
    seedKey(queryKeys.lots(estId), catalogs.lots, (r) => processLotsResponse(r, estId));
    seedKey(queryKeys.herds(estId), catalogs.herds, (r) => processHerdsResponse(r, estId));
}
